import { useCallback, useRef, useState } from "react";
import { Download, FileArchive, FileImage, FileSpreadsheet, FileText, FileVideo, Link2, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { uploadChatAdjunto, getChatAdjuntoUrl, getChatAdjuntoMiniaturaUrl, getApiErrorMessage } from "@/lib/api";
import { compressImageFile } from "@/lib/compressImageFile";
import { extensionArchivo, formatTamano, hostDeUrl } from "@/lib/clienteNombre";
import { cn } from "@/lib/utils";

export const MAX_ADJUNTO_BYTES = 25 * 1024 * 1024;

function iconoArchivo(mime = "", nombre = "") {
  const ext = extensionArchivo(nombre).toLowerCase();
  if (mime.startsWith("image/")) return FileImage;
  if (mime.startsWith("video/")) return FileVideo;
  if (["zip", "rar", "7z", "gz", "tar"].includes(ext)) return FileArchive;
  if (["xls", "xlsx", "csv", "numbers"].includes(ext)) return FileSpreadsheet;
  return FileText;
}

function urlArchivo(a) {
  return getChatAdjuntoUrl(a.id, { descargar: !(a.es_imagen || a.mime === "application/pdf") });
}

/** Fotos grandes del móvil: se reducen antes de subir. El resto de archivos va tal cual. */
async function prepararArchivo(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml" || file.size < 2 * 1024 * 1024) return file;
  try {
    const dataUrl = await compressImageFile(file, { maxEdge: 2560, maxOutputBytes: 2 * 1024 * 1024 });
    const blob = await (await fetch(dataUrl)).blob();
    const ext = blob.type === "image/png" ? "png" : "jpg";
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + "." + ext, { type: blob.type });
  } catch {
    return file;
  }
}

/**
 * Subida de adjuntos con estado por archivo (para el compositor y los trabajos).
 * `items`: { tmpId, nombre, mime, preview, estado: "subiendo"|"ok", meta }
 */
export function useSubidaAdjuntos(clienteId) {
  const [items, setItems] = useState([]);

  const subir = useCallback(
    async (files) => {
      for (const original of Array.from(files || []).slice(0, 20)) {
        if (original.size > MAX_ADJUNTO_BYTES && !original.type.startsWith("image/")) {
          toast.error(`${original.name} supera los 25 MB`);
          continue;
        }
        const tmpId = `${Date.now()}-${Math.random()}`;
        const preview = original.type.startsWith("image/") ? URL.createObjectURL(original) : null;
        setItems((prev) => [...prev, { tmpId, nombre: original.name, mime: original.type, preview, estado: "subiendo" }]);
        try {
          const file = await prepararArchivo(original);
          if (file.size > MAX_ADJUNTO_BYTES) throw new Error(`${original.name} supera los 25 MB`);
          const meta = await uploadChatAdjunto(clienteId, file);
          setItems((prev) => prev.map((a) => (a.tmpId === tmpId ? { ...a, estado: "ok", meta } : a)));
        } catch (err) {
          toast.error(getApiErrorMessage(err, `No se pudo subir ${original.name}`));
          setItems((prev) => prev.filter((a) => a.tmpId !== tmpId));
        }
      }
    },
    [clienteId]
  );

  const quitar = useCallback((tmpId) => setItems((prev) => prev.filter((a) => a.tmpId !== tmpId)), []);
  const reset = useCallback(() => {
    setItems((prev) => {
      prev.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      return [];
    });
  }, []);

  return {
    items,
    subir,
    quitar,
    reset,
    subiendo: items.some((a) => a.estado === "subiendo"),
    listos: items.filter((a) => a.estado === "ok").map((a) => a.meta),
  };
}

/**
 * Archivo en cuadrado: imagen o miniatura (primera página del PDF) a sangre; si no hay, icono + extensión.
 * Debajo, el nombre (cortado con puntos suspensivos) y el tamaño.
 */
export function AdjuntoTile({ adjunto, preview, subiendo = false, onQuitar, className = "" }) {
  const a = adjunto || {};
  const Icon = iconoArchivo(a.mime, a.nombre);
  const imagen = preview || ((a.es_imagen || a.tiene_miniatura) && a.id ? getChatAdjuntoMiniaturaUrl(a.id) : null);
  const contenido = (
    <>
      <div className="relative aspect-square w-full overflow-hidden rounded-xl border bg-muted/50">
        {imagen ? (
          <img src={imagen} alt="" loading="lazy" className={cn("h-full w-full object-cover", a.mime === "application/pdf" && "object-top")} />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
            <Icon className="h-7 w-7" strokeWidth={1.5} />
            <span className="text-[10px] font-semibold tracking-wider">{extensionArchivo(a.nombre)}</span>
          </div>
        )}
        {imagen && !a.es_imagen && !preview && (
          <span className="absolute left-1.5 top-1.5 rounded bg-background/90 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider shadow-sm">{extensionArchivo(a.nombre)}</span>
        )}
        {subiendo && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>
      <p className="mt-1 truncate text-[11px] leading-tight" title={a.nombre}>{a.nombre}</p>
      {a.tamano ? <p className="text-[10px] text-muted-foreground">{formatTamano(a.tamano)}</p> : null}
    </>
  );
  return (
    <div className={cn("group relative min-w-0", className)}>
      {a.id && !subiendo ? (
        <a href={urlArchivo(a)} target="_blank" rel="noopener noreferrer" className="block">
          {contenido}
        </a>
      ) : (
        <div>{contenido}</div>
      )}
      {onQuitar && (
        <button
          type="button"
          onClick={onQuitar}
          className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-background shadow"
          aria-label="Quitar archivo"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/** Archivo (no imagen) dentro de una burbuja: miniatura arriba si es PDF, nombre cortado y descarga. */
export function AdjuntoMensaje({ adjunto: a, propio }) {
  const Icon = iconoArchivo(a.mime, a.nombre);
  return (
    <a
      href={urlArchivo(a)}
      target="_blank"
      rel="noopener noreferrer"
      className={cn("block w-60 max-w-full overflow-hidden rounded-lg", propio ? "bg-white/15 hover:bg-white/25" : "bg-muted/70 hover:bg-muted")}
    >
      {a.tiene_miniatura && <img src={getChatAdjuntoMiniaturaUrl(a.id)} alt="" loading="lazy" className="h-32 w-full object-cover object-top bg-white" />}
      <div className="flex items-center gap-2.5 px-2.5 py-2">
        <Icon className="h-5 w-5 shrink-0 opacity-70" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm" title={a.nombre}>{a.nombre}</span>
          <span className="block text-[11px] opacity-70">
            {extensionArchivo(a.nombre)} · {formatTamano(a.tamano)}
          </span>
        </span>
        <Download className="h-4 w-4 shrink-0 opacity-60" />
      </div>
    </a>
  );
}

/** Zona para añadir archivos (clic, arrastrar o pegar) con la cuadrícula de los ya añadidos. */
export function AdjuntosEditor({ existentes = [], onQuitarExistente, subida }) {
  const inputRef = useRef(null);
  const [encima, setEncima] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("Files")) {
          e.preventDefault();
          setEncima(true);
        }
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={(e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        setEncima(false);
        subida.subir(e.dataTransfer.files);
      }}
      className={cn("rounded-lg transition", encima && "ring-2 ring-primary/40 bg-primary/5")}
    >
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2.5">
        {existentes.map((a) => (
          <AdjuntoTile key={a.id} adjunto={a} onQuitar={onQuitarExistente ? () => onQuitarExistente(a.id) : undefined} />
        ))}
        {subida.items.map((it) => (
          <AdjuntoTile key={it.tmpId} adjunto={it.meta || { nombre: it.nombre, mime: it.mime }} preview={it.preview} subiendo={it.estado === "subiendo"} onQuitar={() => subida.quitar(it.tmpId)} />
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="aspect-square w-full rounded-xl border border-dashed flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground hover:border-foreground/40"
        >
          <Plus className="h-5 w-5" />
          <span className="text-[10px]">Archivo</span>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          subida.subir(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

/** Enlace en formato chip: título o dominio; clic abre en otra pestaña. */
export function EnlaceChip({ enlace, onQuitar, className = "" }) {
  const etiqueta = enlace.titulo && enlace.titulo !== enlace.url ? enlace.titulo : hostDeUrl(enlace.url);
  return (
    <span className={cn("group inline-flex max-w-full items-center gap-1.5 rounded-full border bg-background pl-2.5 h-8 text-xs hover:border-foreground/30", onQuitar ? "pr-1" : "pr-3", className)}>
      <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <a href={enlace.url} target="_blank" rel="noopener noreferrer" className="truncate font-medium hover:underline" title={enlace.url}>
        {etiqueta}
      </a>
      {onQuitar && (
        <button type="button" onClick={onQuitar} className="ml-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Quitar enlace">
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/** Lista de enlaces como chips + campo para pegar uno nuevo (Enter para añadir). */
export function EnlacesEditor({ enlaces, onChange }) {
  const [url, setUrl] = useState("");
  const añadir = () => {
    const limpio = url.trim();
    if (!limpio) return;
    const href = /^(https?:\/\/|mailto:)/i.test(limpio) ? limpio : `https://${limpio}`;
    if (!enlaces.some((e) => e.url === href)) onChange([...enlaces, { url: href, titulo: "" }]);
    setUrl("");
  };
  return (
    <div className="space-y-2">
      {enlaces.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {enlaces.map((e) => (
            <EnlaceChip key={e.url} enlace={e} onQuitar={() => onChange(enlaces.filter((x) => x.url !== e.url))} />
          ))}
        </div>
      )}
      <div className="relative">
        <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              añadir();
            }
          }}
          onBlur={añadir}
          onPaste={(e) => {
            const pegado = e.clipboardData.getData("text/plain").trim();
            if (/^(https?:\/\/|www\.)\S+$/i.test(pegado)) {
              e.preventDefault();
              const href = pegado.startsWith("http") ? pegado : `https://${pegado}`;
              if (!enlaces.some((x) => x.url === href)) onChange([...enlaces, { url: href, titulo: "" }]);
            }
          }}
          placeholder="Pega un enlace y pulsa Enter"
          className="h-9 pl-9"
        />
      </div>
    </div>
  );
}
